// register vue composition api globally
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import App from './App.vue';
import Toast, { POSITION } from 'vue-toastification';

import 'vue-toastification/dist/index.css';
import './styles/main.scss';
import './styles/tailwind.css';

// Vuetify
import { registerPlugins } from '@/plugins';

// Create app
const app = createApp(App);

// Set up website head
const head = createHead();

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/home' },
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: {
      title: 'Home',
      description:
        'Sports Info Solutions NFL Draft – Big Board Top 5, draft analysis, and SIS Spotlight articles.',
    },
  },
  {
    path: '/big-board',
    name: 'Big Board',
    component: () => import('@/views/BigBoard.vue'),
    meta: {
      title: 'Big Board',
      description: 'SIS NFL Draft Big Board – prospect rankings and draft order.',
    },
  },
  {
    path: '/matrix',
    name: 'Draft Matrix',
    component: () => import('@/views/DraftMatrix.vue'),
    meta: {
      title: 'Draft Matrix',
      description: 'SIS NFL Draft Draft Matrix – draft board and team needs.',
    },
  },
  {
    path: '/stats',
    name: 'Stats',
    component: () => import('@/views/QueryTool.vue'),
    props: true,
    meta: {
      title: 'Stats',
      description: 'NFL Draft combine stats and query tool – filter and compare prospects.',
    },
  },
  {
    path: '/players/:id',
    name: 'Players',
    component: () => import('@/views/PlayerStats.vue'),
    props: true,
    meta: {
      title: 'Player Stats',
      description: 'NFL Draft prospect player stats and profile.',
    },
  },
  {
    path: '/glossary',
    name: 'Glossary',
    component: () => import('@/views/Glossary.vue'),
    meta: {
      title: 'Glossary',
      description: 'SIS NFL Draft glossary – grading scales and trait definitions for prospect evaluation.',
    },
  },
  {
    path: '/scouts',
    name: 'Scouts',
    component: () => import('@/views/Scouts.vue'),
    meta: {
      title: 'Our Scouts',
      description: 'Meet the Sports Info Solutions scouts behind our NFL Draft analysis.',
    },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: import('@/views/Terms.vue'),
    meta: {
      title: 'Terms of Use',
      description:
        'Terms of use for nfldraft.sportsinfosolutions.com – Sports Info Solutions NFL Draft site.',
    },
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: import('@/views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy',
      description: 'Privacy policy for Sports Info Solutions NFL Draft website.',
    },
  },
  { path: '/:pathMatch(.*)*', redirect: '/home' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const pinia = createPinia();

app.use(pinia).use(head).use(router);
app.use(Toast, {
  transition: 'Vue-Toastification__bounce',
  maxToasts: 20,
  newestOnTop: true,
  position: POSITION.BOTTOM_CENTER,
  timeout: 3000,
});
registerPlugins(app);

// Mount app
app.mount('#sis-nfl-draft');
